
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandEmpty,
} from '@/components/ui/command';
import { SearchResult, SearchResults as SearchResultsType } from '@/hooks/useGlobalSearch';

interface SearchResultsProps {
  results: SearchResultsType;
  closeDialog: () => void;
  query: string;
  onSelect?: (type: string, id: string) => void;
  isLoading?: boolean;
}

const SearchResults: React.FC<SearchResultsProps> = ({ 
  results, 
  closeDialog, 
  query,
  onSelect,
  isLoading 
}) => {
  const navigate = useNavigate();

  const handleFamilyClick = (family: SearchResult) => {
    if (onSelect) {
      onSelect('families', family.id);
    } else {
      navigate(`/admin/families/${family.id}`);
      closeDialog();
    }
  };

  const handleStudentClick = (student: SearchResult) => {
    if (onSelect) {
      onSelect('students', student.id);
    } else {
      navigate(`/admin/students/${student.id}`);
      closeDialog();
    }
  };

  const handleClassClick = (classItem: SearchResult) => {
    if (onSelect) {
      onSelect('classes', classItem.id);
    } else {
      navigate(`/admin/classes/${classItem.id}`);
      closeDialog();
    }
  };

  const noResults = !results.families.length && !results.students.length && 
                    !results.classes.length && !results.events.length && 
                    !results.staffMembers.length;

  // Debug: Log what's being received for staff members
  console.log("Staff search results in component:", results.staffMembers);

  return (
    <CommandList className="max-h-[300px] overflow-y-auto">
      {noResults && query.length > 0 ? (
        <CommandEmpty>No results found for "{query}"</CommandEmpty>
      ) : null}

      {results.families.length > 0 && (
        <CommandGroup heading="Families">
          {results.families.map((family) => (
            <CommandItem
              key={family.id}
              onSelect={() => handleFamilyClick(family)}
            >
              {family.name}
            </CommandItem>
          ))}
        </CommandGroup>
      )}

      {results.students.length > 0 && (
        <>
          {results.families.length > 0 && <CommandSeparator />}
          <CommandGroup heading="Students">
            {results.students.map((student) => (
              <CommandItem
                key={student.id}
                onSelect={() => handleStudentClick(student)}
              >
                {student.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </>
      )}

      {results.classes.length > 0 && (
        <>
          {(results.families.length > 0 || results.students.length > 0) && <CommandSeparator />}
          <CommandGroup heading="Classes">
            {results.classes.map((classItem) => (
              <CommandItem
                key={classItem.id}
                onSelect={() => handleClassClick(classItem)}
              >
                {classItem.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </>
      )}

      {results.events.length > 0 && (
        <>
          {(results.families.length > 0 || results.students.length > 0 || results.classes.length > 0) && (
            <CommandSeparator />
          )}
          <CommandGroup heading="Events">
            {results.events.map((event) => (
              <CommandItem
                key={event.id}
                onSelect={() => onSelect ? onSelect('events', event.id) : null}
              >
                {event.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </>
      )}

      {results.staffMembers.length > 0 && (
        <>
          {(results.families.length > 0 ||
            results.students.length > 0 ||
            results.classes.length > 0 ||
            results.events.length > 0) && <CommandSeparator />}
          <CommandGroup heading="Staff">
            {results.staffMembers.map((staff) => (
              <CommandItem
                key={staff.id}
                onSelect={() => {
                  if (onSelect) {
                    onSelect('staffMembers', staff.id);
                  } else {
                    navigate(`/admin/staff/${staff.id}`);
                    closeDialog();
                  }
                }}
              >
                {staff.name}
              </CommandItem>
            ))}
          </CommandGroup>
        </>
      )}
    </CommandList>
  );
};

export default SearchResults;
